import React from 'react';
import { Sun, TrendingUp, Target, Award } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import DashboardCard from './DashboardCard';
import useAuth from '@/hooks/useAuth';

const SunStatsWidget = () => {
  const { user, profile } = useAuth();
  
  if (!user || !profile || !profile.sunnyStats) {
    return (
      <DashboardCard
        title="Your Sunny Stats"
        description="Track your vitamin D intake"
        icon={<Sun className="h-5 w-5" />}
        isPlaceholder
        placeholderText="Login Required"
      >
        <div className="text-center py-6 text-muted-foreground">
          Please log in to view your stats
        </div>
      </DashboardCard>
    );
  }
  
  const { sunnyStats } = profile;
  const weeklyGoal = 10; // Hours of sun per week
  const weeklyProgress = Math.min((sunnyStats.hoursThisWeek / weeklyGoal) * 100, 100);
  const isGoalMet = weeklyProgress >= 100;

  return (
    <DashboardCard
      title="Your Sunny Stats"
      description="Track your vitamin D intake"
      icon={<Sun className="h-5 w-5" />}
      variant={isGoalMet ? "success" : "default"}
    >
      <div className="space-y-4">
        {/* Weekly Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium">Weekly Goal Progress</span>
            <span className="text-sm text-muted-foreground">
              {sunnyStats.hoursThisWeek}/{weeklyGoal}h
            </span>
          </div>
          <Progress 
            value={weeklyProgress} 
            className="h-3" 
          />
          <div className="flex justify-between text-xs text-muted-foreground mt-1">
            <span>0h</span>
            <span>{Math.round(weeklyProgress)}%</span>
            <span>{weeklyGoal}h</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 pt-2">
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <TrendingUp className="h-4 w-4 text-peach-orange" />
            </div>
            <div className="text-lg font-bold">{sunnyStats.hoursThisMonth}</div>
            <div className="text-xs text-muted-foreground">Month</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Target className="h-4 w-4 text-coral-pink" />
            </div>
            <div className="text-lg font-bold">{sunnyStats.visitCount}</div>
            <div className="text-xs text-muted-foreground">Visits</div>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center mb-1">
              <Award className="h-4 w-4 text-sunset-yellow" />
            </div>
            <div className="text-lg font-bold">3</div>
            <div className="text-xs text-muted-foreground">Streak</div>
          </div>
        </div>

        {/* Encouragement Message */}
        {isGoalMet ? (
          <div className="p-3 bg-emerald-50/50 rounded-lg border border-emerald-200/30">
            <div className="text-sm font-medium text-emerald-700">
              🎉 Weekly goal achieved!
            </div>
          </div>
        ) : (
          <div className="p-3 bg-peach-orange/10 rounded-lg border border-peach-orange/20">
            <div className="text-sm font-medium text-sunset-dark">
              {weeklyGoal - sunnyStats.hoursThisWeek > 0 
                ? `${(weeklyGoal - sunnyStats.hoursThisWeek).toFixed(1)}h more to reach your weekly goal!`
                : "You've exceeded your weekly goal! 🌟"
              }
            </div>
          </div>
        )}
      </div>
    </DashboardCard>
  );
};

export default SunStatsWidget;